<template>
  <label
    class="radio-button"
    :class="[
      {
        'radio-button--checked': isChecked,
        'radio-button--disabled': disabled,
        'radio-button--button-style': buttonStyle,
      },
      size ? `radio-button--${size}` : '',
      color ? `radio-button--color-${color}` : '',
    ]"
    :aria-label="ariaLabel || label"
    :aria-checked="isChecked"
    :aria-disabled="disabled"
    :tabindex="disabled ? -1 : 0"
    @keydown.enter="model = radioValue"
    @keydown.space.prevent="model = radioValue"
  >
    <input
      v-bind="props"
      v-model="model"
      type="radio"
      :value="radioValue"
      data-hj-suppress
    />
    <span
      v-if="label || $slots.default"
      :class="['radio-button-label', { 'radio-button-label--with-logo': logo }]"
    >
      <slot
        :label="label"
        :checked="isChecked"
      >
        {{ label }}
      </slot>

      <img
        v-if="logo"
        :src="logo"
        class="rbl-logo"
      />
    </span>
  </label>
</template>

<script setup lang="ts">
import type { InputFieldProps } from '@/mixins/InputFieldAttributes';

const props = defineProps<
  InputFieldProps & {
    label?: string;
    radioValue?: string;
    size?: string;
    color?: string;
    buttonStyle?: boolean;
    logo?: string;
    ariaLabel?: string;
  }
>();

const isChecked = computed(() => props.value === props.radioValue);

const model = defineModel<string>('value');
</script>

<style lang="scss" scoped>
.radio-button {
  position: relative;
  display: flex;
  cursor: pointer;

  // Radio button
  &:before {
    content: '';
    position: relative;
    display: inline-block;
    flex: 0 0 1.25 * $pad;
    top: 2px;
    width: 1.25 * $pad;
    height: 1.25 * $pad;
    background-color: $color-general-background-neutral-default;
    border: map-get($border-width, 's') solid $color-action-border-primary-default-base;
    border-radius: 100%;
  }

  // Radio button checked indicator
  &:after {
    content: '';
    position: absolute;
    display: block;
    width: 0.75 * $pad;
    height: 0.75 * $pad;
    top: 6px;
    left: 4px;
    opacity: 0;
    transition: all 0.1s ease-in-out;
    background-color: $color-action-background-primary-default-base;
    border-radius: 100%;
  }

  input {
    display: none;
  }

  .radio-button-label {
    position: relative;
    margin-top: 3px;
    flex: 1 0 auto;
    margin-left: $pad * 0.5;
    font-size: map-get($font-sizes, 'l');

    &.radio-button-label--with-logo {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 10px;
      width: fit-content;

      .rbl-logo {
        max-width: 100px;
        max-height: 20px;
      }
    }
  }

  &.radio-button--checked {
    &:after {
      opacity: 1;
    }
  }

  &.radio-button--disabled {
    opacity: 0.65;
    pointer-events: none;
  }

  &.radio-button--l {
    &:before {
      width: $space;
      height: $space;
      flex: 0 0 $space;
      top: 0;
    }

    &:after {
      width: ($space * 0.5);
      height: ($space * 0.5);
      top: 6px;
      left: 6px;
    }
  }

  &.radio-button--button-style {
    flex: 1;
    justify-content: center;
    align-content: center;
    padding: $pad 0 math.div($pad, 2);
    background-color: $color-general-background-neutral-default;
    border: 1px solid palette(grey, metallic);
    border-radius: map-get($border-radiuses, 'xl');
    transition:
      0.2s ease-in-out background-color,
      0.2s ease-in-out border;
    user-select: none;

    .radio-button-label,
    .icon {
      transition: 0.2s ease-in-out color;
    }

    .radio-button-label {
      display: flex;
      flex: 1;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      margin: 0;
      grid-gap: $pad/2;

      &.radio-button-label--with-logo {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 10px;
        width: fit-content;

        .rbl-logo {
          max-width: 100px;
          max-height: 20px;
        }
      }
    }

    &:before,
    &:after {
      display: none;
    }

    &:hover,
    &.radio-button--checked {
      background-color: $color-action-background-primary-default-base;
      border-color: $color-action-background-primary-default-base;

      .radio-button-label,
      :deep(.icon) {
        color: $color-general-text-inverse-default;
      }
    }
  }

  // Colors
  @each $palette, $tones in $palettes {
    @each $tone, $hex in $tones {
      @if $tone == 'base' {
        &.radio-button--color-#{$palette} {
          &:after {
            background-color: $hex;
          }
        }
      } @else {
        &.radio-button--color-#{$palette}-#{$tone} {
          &:after {
            background-color: $hex;
          }
        }
      }
    }
  }
}
</style>
